import axios from 'axios'
import type { AxiosError, InternalAxiosRequestConfig } from 'axios'

const API_BASE = import.meta.env.VITE_API_BASE || '/api/v1'

export const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
  withCredentials: true, // 重要：允许发送 Cookie
})

// 请求拦截器 - 不再需要手动添加 API Key，改用 Cookie 认证
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    return config
  },
  (error) => Promise.reject(error)
)

// 响应拦截器 - 统一错误处理
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || ''
      const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/me')
      const isLoginPage = typeof window !== 'undefined' && window.location.pathname.includes('/login')
      if (!isAuthEndpoint && !isLoginPage && typeof window !== 'undefined') {
        window.location.href = '/app/login'
      }
    }
    return Promise.reject(error)
  }
)

// 类型定义
export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

export interface MarkdownLog {
  id: string
  session_id: string | null
  agent_type: string
  agent_name?: string | null
  log_date: string
  file_path: string
  file_hash: string | null
  front_matter: Record<string, any>
  title: string | null
  summary: string | null
  created_at: string
  updated_at: string
  content?: string
}

export interface MarkdownLogSearchParams {
  agent_type?: string
  start_date?: string
  end_date?: string
  tags?: string[]
  query?: string
  session_id?: string
  page?: number
  page_size?: number
}

export interface MarkdownStats {
  total_logs: number
  total_chars: number
  by_agent: Record<string, number>
  by_date: Record<string, number>
  top_tags: Array<{ tag: string; count: number }>
}

export type ClipStatus = 'published' | 'draft' | 'archived'
export type ClipSourceType = 'web' | 'video' | 'note' | 'other'

export interface Clip {
  id: string
  agent_id: string | null
  agent_name: string | null
  title: string
  summary: string | null
  source_url: string | null
  source_type: ClipSourceType
  cover_image: string | null
  category: string | null
  tags: string[]
  status: ClipStatus
  created_at: string
  updated_at: string
  content?: string
}

export interface ClipSearchParams {
  agent_name?: string
  category?: string
  tag?: string
  source_type?: ClipSourceType
  status?: ClipStatus
  query?: string
  page?: number
  page_size?: number
}

export interface ClipStats {
  total_clips: number
  by_category: Record<string, number>
  by_agent: Record<string, number>
  by_source_type: Record<string, number>
  top_tags: Array<{ tag: string; count: number }>
}

// API 方法 - 所有方法直接返回数据（已解包 AxiosResponse）
export const markdownApi = {
  create: async (data: { content: string; session_id?: string; agent_type: string; log_date?: string; front_matter?: any }) =>
    (await api.post<MarkdownLog>('/markdown', data)).data,

  list: async (params: MarkdownLogSearchParams) =>
    (await api.get<PaginatedResponse<MarkdownLog>>('/markdown', { params })).data,

  stats: async (start_date?: string, end_date?: string) =>
    (await api.get<MarkdownStats>('/markdown/stats', { params: { start_date, end_date } })).data,

  get: async (id: string) =>
    (await api.get<MarkdownLog>(`/markdown/${id}`)).data,

  update: async (id: string, data: Partial<Pick<MarkdownLog, 'title' | 'summary' | 'content'> & { tags?: string[] }>) =>
    (await api.put<MarkdownLog>(`/markdown/${id}`, data)).data,

  delete: async (id: string) => {
    await api.delete(`/markdown/${id}`)
  },

  batchImport: async (files: File[]) => {
    const formData = new FormData()
    files.forEach((f) => formData.append('files', f))
    return (await api.post('/markdown/batch-import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data
  },
}

export const clipApi = {
  create: async (data: Partial<Clip> & { title: string; content: string }) =>
    (await api.post<Clip>('/clips', data)).data,

  list: async (params: ClipSearchParams) =>
    (await api.get<PaginatedResponse<Clip>>('/clips', { params })).data,

  stats: async () =>
    (await api.get<ClipStats>('/clips/stats')).data,

  get: async (id: string) =>
    (await api.get<Clip>(`/clips/${id}`)).data,

  update: async (id: string, data: Partial<Clip>) =>
    (await api.put<Clip>(`/clips/${id}`, data)).data,

  delete: async (id: string) => {
    await api.delete(`/clips/${id}`)
  },
}

export const healthApi = {
  check: async () =>
    (await api.get('/health/public')).data,
}

export const agentApi = {
  list: async (params?: { page?: number; page_size?: number; is_active?: boolean }) =>
    (await api.get<PaginatedResponse<AgentResponse>>('/agents', { params })).data,

  listReadable: async (params?: { page?: number; page_size?: number }) =>
    (await api.get<PaginatedResponse<AgentResponse>>('/agents/readable', { params })).data,

  get: async (id: string) =>
    (await api.get<AgentResponse>(`/agents/${id}`)).data,

  me: async () =>
    (await api.get<AgentResponse>(`/agents/me`)).data,

  create: async (data: AgentCreate) =>
    (await api.post<AgentResponse>('/agents', data)).data,

  update: async (id: string, data: AgentUpdate) =>
    (await api.patch<AgentResponse>(`/agents/${id}`, data)).data,

  updatePermissions: async (id: string, data: { permissions: string[]; readable_agent_ids?: string[] }) =>
    (await api.patch<AgentResponse>(`/agents/${id}/permissions`, data)).data,

  rotateKey: async (id: string) =>
    (await api.post<AgentResponse>(`/agents/${id}/rotate-key`)).data,

  revealKey: async (id: string) =>
    (await api.get<AgentResponse>(`/agents/${id}/key`)).data,

  delete: async (id: string) => {
    await api.delete(`/agents/${id}`)
  },
}

export interface AgentResponse {
  id: string
  name: string
  display_name: string
  description: string | null
  agent_type: string
  permissions: string[]
  readable_agent_ids: string[]
  is_active: boolean
  created_at: string
  last_used_at: string | null
  api_key?: string
}

export interface AgentCreate {
  name: string
  display_name: string
  description?: string
  permissions: string[]
  readable_agent_ids: string[]
  is_active: boolean
}

export interface AgentUpdate {
  display_name?: string
  description?: string
  agent_type?: string
  permissions?: string[]
  readable_agent_ids?: string[]
  is_active?: boolean
}

export interface LoginRequest {
  username: string
  password: string
}

export interface LoginResponse {
  user: AdminUserResponse
  token: string
}

export interface AdminUserResponse {
  id: string
  username: string
  display_name: string
  email: string | null
  is_superuser: boolean
  is_active: boolean
}

export const authApi = {
  login: async (data: LoginRequest) =>
    (await api.post<{ user: AdminUserResponse; token: string }>('/auth/login', data)).data,

  logout: async () =>
    (await api.post('/auth/logout')).data,

  me: async () =>
    (await api.get<{ id: string; username: string; display_name: string; email: string | null; is_superuser: boolean; is_active: boolean }>('/auth/me')).data,

  changePassword: async (data: { old_password: string; new_password: string }) =>
    (await api.post('/auth/change-password', data)).data,
}

